import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me", { withCredentials: true });
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AdminCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {}, { withCredentials: true });
    } catch (error) {
      // Logout best-effort: even if the backend call fails (network, expired cookie),
      // we still clear local state so the user is signed out client-side.
      console.error("Logout request failed:", error);
    }
    setUser(null);
    window.location.href = "/admin/login";
  }, []);

  const value = useMemo(
    () => ({ user, loading, checkAuth, login, logout, setUser }),
    [user, loading, checkAuth, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  event_admin: "Event Admin",
  sponsorship_team: "Sponsorship Team",
  volunteer_coordinator: "Volunteer Coordinator",
  view_only: "View Only",
};

export const canEdit = (user) => ["super_admin", "event_admin", "sponsorship_team", "volunteer_coordinator"].includes(user?.role);
export const isSuperAdmin = (user) => user?.role === "super_admin";
